import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu as MenuIcon, X, Phone } from 'lucide-react';
import { LogoMark } from './LogoMark';
import { DialogPortal } from '../ui/DialogPortal';
import { NAV_ITEMS, HEADER_CTA } from '../../data/site';
import { useDialog } from '../../hooks/useDialog';

/**
 * One header shell: sticky, 64px on mobile and 72px from `lg`, one line at
 * 320px. Navigation collapses below `lg` into a slide-over panel that locks
 * scroll, traps focus, closes on Escape and offers a close control at both
 * the top and the foot.
 *
 * The current page is marked by an underline as well as colour, and
 * `NavLink` sets `aria-current="page"` for us.
 */
export function SiteHeader() {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const {
    pathname
  } = useLocation();
  const closePanel = () => setIsPanelOpen(false);
  const panelRef = useDialog({
    isOpen: isPanelOpen,
    onClose: closePanel
  });

  // Close on navigation, so following a link never leaves the panel over
  // the page it just loaded.
  useEffect(() => {
    setIsPanelOpen(false);
  }, [pathname]);

  // Close the panel if the viewport grows past the collapse point, so the
  // nav is never both expanded and duplicated.
  useEffect(() => {
    if (!isPanelOpen) return;
    const query = window.matchMedia('(min-width: 1024px)');
    const onChange = () => {
      if (query.matches) setIsPanelOpen(false);
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [isPanelOpen]);
  return <header data-component-id="C02" role="banner" className="fixed inset-x-0 top-0 z-40 border-b border-hairline bg-cream/95 backdrop-blur-md" data-id="element-348" data-mp="27c59hmiejwb7">
      <div className="mx-auto flex h-header-sm max-w-container items-center justify-between gap-4 px-gutter-sm sm:px-gutter-md lg:h-header-lg lg:px-gutter-lg" data-id="element-349" data-mp="2c588ia5y7xys">
        <LogoMark data-id="element-350" data-mp="1jlrq0kdvv5s8" />

        <nav aria-label="Main navigation" className="hidden lg:block" data-id="element-351" data-mp="1jl9k0w1f5d74">
          <ul className="flex items-center gap-0.5" data-id="element-352" data-mp="2mwp56umthrt9">
            {NAV_ITEMS.map(item => <li key={item.to} data-id="element-353" data-mp="2hxuel6ilyyyl">
                <NavLink to={item.to} end={item.to === '/'} className={({
              isActive
            }) => `relative block rounded-control px-3 py-2 text-sm font-bold uppercase tracking-wide transition-colors duration-200 ease-out-expo focus:outline-none focus:ring-2 focus:ring-moss focus:ring-offset-2 ${isActive ? 'text-ink' : 'text-ink-muted hover:bg-moss/10 hover:text-ink'}`} data-id="element-354" data-mp="2hwt0sua7jyd6">
                  {({
                isActive
              }) => <>
                      {item.label}
                      {isActive && <span aria-hidden="true" className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-flame" data-id="element-355" data-mp="3ogskp8c8gycx" />}
                    </>}
                </NavLink>
              </li>)}
          </ul>
        </nav>

        <div className="flex shrink-0 items-center gap-2" data-id="element-356" data-mp="3hzikvr4jfky8">
          {/* An anchor, not a Link: this dials rather than navigates. */}
          <a href={HEADER_CTA.href} aria-label={HEADER_CTA.ariaLabel} className="inline-flex items-center gap-2 rounded-container bg-flame px-3 py-2.5 text-xs font-black uppercase tracking-wide text-white transition-colors duration-200 ease-out-expo hover:bg-flame-dark focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2 sm:px-5 sm:text-sm" data-id="element-357" data-mp="3my2aq2m95gvv">
            <Phone className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-358" data-mp="9p7b13koljpf" />
            {/* Short label at 320px so the bar never wraps. */}
            <span className="sm:hidden" data-id="element-359" data-mp="127bdf027e6bf">{HEADER_CTA.shortLabel}</span>
            <span className="hidden sm:inline" data-id="element-360" data-mp="129ea22xv2xp4">{HEADER_CTA.label}</span>
          </a>

          <button onClick={() => setIsPanelOpen(true)} aria-expanded={isPanelOpen} aria-controls="mobile-nav-panel" aria-label="Open navigation menu" className="inline-flex h-11 w-11 items-center justify-center rounded-container border border-control text-ink transition-colors duration-200 ease-out-expo hover:bg-ink/5 focus:outline-none focus:ring-2 focus:ring-moss focus:ring-offset-2 lg:hidden" data-id="element-361" data-mp="39shac3cul5uv">
            <MenuIcon className="h-6 w-6" aria-hidden="true" data-id="element-362" data-mp="3ohdehpbs9yq7" />
          </button>
        </div>
      </div>

      {/* Portal stays mounted so its container exists before the panel
          opens, and the focus effect never races it. */}
      <DialogPortal data-id="element-363" data-mp="3eo4xyebolqy9">
        {isPanelOpen && <div className="lg:hidden" data-id="element-364" data-mp="g0iovbjzyau2">
            <div className="fixed inset-0 z-50 bg-ink-deep/70 backdrop-blur-sm" onClick={closePanel} aria-hidden="true" data-id="element-365" data-mp="g1k0pi8u71cu" />
            <div ref={panelRef} id="mobile-nav-panel" role="dialog" aria-modal="true" aria-label="Navigation menu" className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xs flex-col border-l border-hairline bg-cream shadow-2xl" data-id="element-366" data-mp="ilyoy2vwkzem">
              <div className="flex h-header-sm shrink-0 items-center justify-between border-b border-hairline px-gutter-sm" data-id="element-367" data-mp="xfuww0o5bq1b">
                <LogoMark asText data-id="element-368" data-mp="16icj4f3swiob" />
                <button onClick={closePanel} aria-label="Close navigation menu" className="inline-flex h-11 w-11 items-center justify-center rounded-container border border-control text-ink transition-colors duration-200 ease-out-expo hover:bg-ink/5 focus:outline-none focus:ring-2 focus:ring-moss focus:ring-offset-2" data-id="element-369" data-mp="16hkph9trkv0o">
                  <X className="h-6 w-6" aria-hidden="true" data-id="element-370" data-mp="1pa5dodyiqby5" />
                </button>
              </div>

              {/* Order is identical to desktop, never reordered for mobile. */}
              <nav aria-label="Mobile navigation" className="flex-1 overflow-y-auto px-gutter-sm py-4" data-id="element-371" data-mp="3hbg4tmfchq8j">
                <ul data-id="element-372" data-mp="3v462wp85elm0">
                  {NAV_ITEMS.map(item => <li key={item.to} data-id="element-373" data-mp="3qajp2tq90bom">
                      <NavLink to={item.to} end={item.to === '/'} onClick={closePanel} className={({
                  isActive
                }) => `flex items-center justify-between rounded-container px-3 py-3 text-base font-bold uppercase tracking-wide transition-colors duration-200 ease-out-expo focus:outline-none focus:ring-2 focus:ring-moss ${isActive ? 'bg-moss/15 text-ink' : 'text-ink/75 hover:bg-moss/10 hover:text-ink'}`} data-id="element-374" data-mp="3q9ibahhulb37">
                        {({
                    isActive
                  }) => <>
                            {item.label}
                            {isActive && <span className="text-xs font-black text-flame" data-id="element-375" data-mp="1vqi1vjk5riur">
                                <span className="sr-only" data-id="element-376" data-mp="20puqa8qogxzg">Current page</span>
                                <span aria-hidden="true" data-id="element-377" data-mp="1ltditslm03i6">●</span>
                              </span>}
                          </>}
                      </NavLink>
                    </li>)}
                </ul>

                <a href={HEADER_CTA.href} aria-label={HEADER_CTA.ariaLabel} onClick={closePanel} className="mt-4 flex w-full items-center justify-center gap-2 rounded-container bg-flame px-4 py-3.5 text-base font-black uppercase tracking-wide text-white transition-colors duration-200 ease-out-expo hover:bg-flame-dark focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2" data-id="element-378" data-mp="1curxxqso0ce8">
                  <Phone className="h-5 w-5" aria-hidden="true" data-id="element-379" data-mp="jmazrkjm4kb3" />
                  {HEADER_CTA.label}
                </a>
              </nav>

              {/* Second close control at the foot: scrolled to the bottom of
                  a long panel, the top control is off-screen. */}
              <div className="shrink-0 border-t border-hairline p-gutter-sm" data-id="element-380" data-mp="5v5xsbsrvfxl">
                <button onClick={closePanel} className="w-full rounded-container border border-control px-4 py-3 text-sm font-bold uppercase tracking-wide text-ink transition-colors duration-200 ease-out-expo hover:bg-ink/5 focus:outline-none focus:ring-2 focus:ring-moss focus:ring-offset-2" data-id="element-381" data-mp="v0blb59ulmw">
                  Close menu
                </button>
              </div>
            </div>
          </div>}
      </DialogPortal>
    </header>;
}